import type { QuoteStatus } from '@cortex/domain';
import type { StatusBadgeTone } from './StatusBadge';

/**
 * The canonical quote lifecycle — single source of truth for the UI.
 *
 * Mirrors `QuoteStatusSchema` in `@cortex/domain` and is ordered to match the
 * real pipeline progression (Draft → Quoted → Bound, with Referred / Expired
 * as the two off-ramps). Anything that renders, filters, or charts a quote
 * status reads from here so the vocabulary, colours, and ordering stay
 * identical across the workbench, pipeline, and admin surfaces.
 */
export const QUOTE_STATUSES: readonly QuoteStatus[] = [
  'Draft',
  'Quoted',
  'Bound',
  'Referred',
  'Expired',
];

/** StatusBadge tone for a quote status. */
export const toneForQuoteStatus = (status: QuoteStatus): StatusBadgeTone => {
  switch (status) {
    case 'Draft':
      return 'neutral';
    case 'Quoted':
      return 'info';
    case 'Bound':
      return 'active';
    case 'Referred':
      return 'warning';
    case 'Expired':
      return 'danger';
  }
};

/**
 * Theme-token colour for a quote status, returned as a `var(--token)` string so
 * charts (SVG fills / strokes) share the exact palette as {@link toneForQuoteStatus}.
 */
export const quoteStatusColorVar = (status: QuoteStatus): string => {
  switch (status) {
    case 'Draft':
      return 'var(--muted-foreground)';
    case 'Quoted':
      return 'var(--info)';
    case 'Bound':
      return 'var(--success)';
    case 'Referred':
      return 'var(--warning)';
    case 'Expired':
      return 'var(--destructive)';
  }
};
